import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Badge,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Person as PersonIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import {
  portalAdminApi,
  AdminKonversation,
  AdminPortalNachricht,
} from '../services/portalService';

const PortalNachrichtenAdmin: React.FC = () => {
  const [konversationen, setKonversationen] = useState<AdminKonversation[]>([]);
  const [selectedKonversation, setSelectedKonversation] = useState<AdminKonversation | null>(null);
  const [nachrichten, setNachrichten] = useState<AdminPortalNachricht[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingNachrichten, setLoadingNachrichten] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [antwortText, setAntwortText] = useState('');
  const [antwortBetreff, setAntwortBetreff] = useState('');
  const [sending, setSending] = useState(false);
  const [antwortDialogOpen, setAntwortDialogOpen] = useState(false);

  useEffect(() => {
    loadKonversationen();
  }, []);

  const loadKonversationen = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await portalAdminApi.getKonversationen();
      setKonversationen(response.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Konversationen:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Konversationen');
    } finally {
      setLoading(false);
    }
  };

  const loadNachrichten = async (konversation: AdminKonversation) => {
    try {
      setLoadingNachrichten(true);
      setSelectedKonversation(konversation);
      const response = await portalAdminApi.getKonversation(konversation.adresseID);
      setNachrichten(response.data);
      // Update unread count in list
      setKonversationen(prev =>
        prev.map(k =>
          k.adresseID === konversation.adresseID ? { ...k, anzahlUngelesen: 0 } : k
        )
      );
    } catch (err: any) {
      console.error('Fehler beim Laden der Nachrichten:', err);
    } finally {
      setLoadingNachrichten(false);
    }
  };

  const handleSendAntwort = async () => {
    if (!selectedKonversation || !antwortText.trim()) return;

    try {
      setSending(true);
      const response = await portalAdminApi.antwortSenden({
        adresseID: selectedKonversation.adresseID,
        betreff: antwortBetreff || 'Antwort',
        inhalt: antwortText,
      });
      setNachrichten([...nachrichten, response.data]);
      setAntwortText('');
      setAntwortBetreff('');
      setAntwortDialogOpen(false);
      // Refresh conversation list
      loadKonversationen();
    } catch (err: any) {
      console.error('Fehler beim Senden der Antwort:', err);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Gestern';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('de-DE', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
    }
  };

  const formatFullDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const totalUngelesen = konversationen.reduce((sum, k) => sum + k.anzahlUngelesen, 0);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          {selectedKonversation && (
            <IconButton onClick={() => setSelectedKonversation(null)}>
              <BackIcon />
            </IconButton>
          )}
          <Typography variant="h4">
            {selectedKonversation ? selectedKonversation.adresseName : 'Portal-Nachrichten'}
          </Typography>
          {!selectedKonversation && totalUngelesen > 0 && (
            <Chip
              label={`${totalUngelesen} ungelesen`}
              color="error"
              size="small"
            />
          )}
        </Box>
        <IconButton onClick={loadKonversationen} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box display="flex" gap={3} sx={{ height: 'calc(100vh - 200px)' }}>
        {/* Konversations-Liste */}
        <Paper
          sx={{
            width: selectedKonversation ? 300 : '100%',
            maxWidth: selectedKonversation ? 300 : 600,
            overflow: 'auto',
            display: selectedKonversation ? { xs: 'none', md: 'block' } : 'block',
          }}
        >
          {konversationen.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography color="textSecondary">
                Keine Nachrichten vorhanden
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {konversationen.map((konv, index) => (
                <React.Fragment key={konv.adresseID}>
                  {index > 0 && <Divider />}
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={selectedKonversation?.adresseID === konv.adresseID}
                      onClick={() => loadNachrichten(konv)}
                    >
                      <ListItemAvatar>
                        <Badge
                          badgeContent={konv.anzahlUngelesen}
                          color="error"
                          invisible={konv.anzahlUngelesen === 0}
                        >
                          <Avatar sx={{ bgcolor: konv.anzahlUngelesen > 0 ? 'primary.main' : 'grey.400' }}>
                            <PersonIcon />
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body1"
                            fontWeight={konv.anzahlUngelesen > 0 ? 'bold' : 'normal'}
                          >
                            {konv.adresseName}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              noWrap
                              sx={{ maxWidth: 180 }}
                            >
                              {konv.letzteNachrichtVomPortal ? '' : 'Sie: '}
                              {konv.letzterBetreff}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {formatDate(konv.letzteNachrichtAm)} - {konv.anzahlNachrichten} Nachrichten
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>

        {/* Nachrichten-Verlauf */}
        {selectedKonversation && (
          <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {loadingNachrichten ? (
              <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Nachrichten */}
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  {nachrichten.map((nachricht) => (
                    <Box
                      key={nachricht.portalNachrichtID}
                      sx={{
                        display: 'flex',
                        justifyContent: nachricht.vomPortalBenutzer ? 'flex-start' : 'flex-end',
                      }}
                    >
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          maxWidth: '70%',
                          bgcolor: nachricht.vomPortalBenutzer ? 'grey.100' : 'primary.light',
                          color: nachricht.vomPortalBenutzer ? 'text.primary' : 'primary.contrastText',
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Avatar sx={{ width: 24, height: 24, fontSize: '0.8rem' }}>
                            {nachricht.vomPortalBenutzer ? <PersonIcon fontSize="small" /> : <BusinessIcon fontSize="small" />}
                          </Avatar>
                          <Typography variant="caption" fontWeight="bold">
                            {nachricht.absenderName || (nachricht.vomPortalBenutzer ? 'Kunde' : 'Mitarbeiter')}
                          </Typography>
                          <Typography variant="caption">
                            {formatFullDate(nachricht.gesendetAm)}
                          </Typography>
                        </Box>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          {nachricht.betreff}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {nachricht.inhalt}
                        </Typography>
                      </Paper>
                    </Box>
                  ))}
                </Box>

                {/* Antwort-Button */}
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={() => setAntwortDialogOpen(true)}
                    fullWidth
                  >
                    Antwort schreiben
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        )}
      </Box>

      {/* Antwort-Dialog */}
      <Dialog
        open={antwortDialogOpen}
        onClose={() => setAntwortDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nachricht an {selectedKonversation?.adresseName}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Betreff"
              value={antwortBetreff}
              onChange={(e) => setAntwortBetreff(e.target.value)}
              fullWidth
              placeholder="Antwort"
            />
            <TextField
              label="Nachricht"
              value={antwortText}
              onChange={(e) => setAntwortText(e.target.value)}
              multiline
              rows={6}
              fullWidth
              placeholder="Ihre Nachricht..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAntwortDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleSendAntwort}
            disabled={!antwortText.trim() || sending}
            startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
          >
            Senden
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortalNachrichtenAdmin;
